import {
  AmenitiesAwningAwningIcon,
  AmenitiesBookingBookerDeliveryOnlyIcon,
  AmenitiesBookingSleepsIcon,
  AmenitiesPetsPetFriendlyIcon,
  GeneralCalendarIcon,
  GeneralLengthIcon,
  GeneralUserUserCheckIcon,
  GeneralUserUsersIcon,
} from '@outdoorsyco/bonfire';
import { createSelector } from 'reselect';

import {
  ARROW_LEFT_RIGHT,
  BED,
  BOLT,
  CALENDAR,
  FUEL,
  GLOBE,
  PET_FRIENDLY,
  RUNNING_VEHICLE,
  SETUP_ONLY,
  USER_CHECK,
  USERS,
} from '@/components/switchback/Icon/assets';
import { IconType } from '@/components/switchback/Icon/IconComponent';
import { ECancellationPolicies } from '@/constants/houseRules';
import { TRootState } from '@/redux/rootReducer';
import { ECampsiteCategoryType, ERentalCategory } from '@/services/types/search/rentals/id';
import { getIntl } from '@/utility/i18n';
import { formatMeasurement } from '@/utility/measurements';
import {
  getCampsiteTypeDisplay,
  getStayTypeDisplay,
  getVehicleTypeDisplay,
} from '@/utility/rentals';
import { mapPluralUnitToSingular } from '@/utility/units';

export interface IInfo {
  key: string;
  label: string;
  icon: IconType;
  bonfireIcon: typeof AmenitiesBookingSleepsIcon;
}

interface IListingHeader {
  title: string;
  infos: IInfo[];
  locationPrefix?: string;
  location: string;
  accommodationType: string;
  ratingNumber?: number;
  reviewsCount?: number;
  dealer?: boolean;
  isSuperhost?: boolean;
  campsiteCategoryType?: string;
}

export const getIsRV = (state: TRootState) => state.listing.data?.rental_category === 'rv';
export const getIsStay = (state: TRootState) => state.listing.data?.rental_category === 'stay';
export const getDisallowMovement = (state: TRootState) =>
  state.listing.data?.disallow_movement || false;

export const getSleepCount = (state: TRootState) =>
  state.listing.data?.campsite_category?.max_occupancy ||
  state.listing.data?.stay?.sleeps ||
  state.listing.data?.sleeps;

export const getIsCampground = (state: TRootState) =>
  state.listing.data?.rental_category === 'campground';

export const getIsAuto = (state: TRootState) => state.listing.data?.rental_category === 'auto';

export const getListingHeader = createSelector<
  TRootState,
  TRootState['listing']['data'],
  boolean | undefined,
  IListingHeader | null
>(
  state => state.listing.data,
  getIsAuto,
  (data, isAuto) => {
    if (!data) {
      return null;
    }

    const intl = getIntl();

    const locationPrefix =
      data.rental_category === ERentalCategory.CAMPGROUND
        ? intl.formatMessage({ defaultMessage: 'Campground in', id: '7C1LoC' })
        : undefined;

    const location = `${data.location.city}, ${data.location.state || data.location.county}`;

    const accommodationType: string = (() => {
      if (data.rental_category === ERentalCategory.CAMPGROUND) {
        return getCampsiteTypeDisplay(data.campsite_category.display_category_type);
      }
      if (data.rental_category === 'stay') {
        return getStayTypeDisplay(data.stay.display_stay_type);
      }
      return getVehicleTypeDisplay(data.display_vehicle_type, isAuto);
    })();

    const sleepsNum: number =
      data.campsite_category?.max_occupancy || data.stay?.sleeps || data.sleeps;
    const bedsNum: number = data.campsite_category?.beds || data.stay?.beds || 0;

    const guests: IInfo | undefined = data.seatbelts
      ? {
          icon: USERS,
          bonfireIcon: GeneralUserUsersIcon,
          label: intl.formatMessage(
            { defaultMessage: '{guests, plural, one {# guest} other {# guests}}', id: '1ahDyv' },
            { guests: data.seatbelts },
          ),
          key: 'guests',
        }
      : undefined;
    const cancellation: IInfo | undefined =
      data.cancel_policy === ECancellationPolicies.FLEXIBLE
        ? {
            icon: CALENDAR,
            bonfireIcon: GeneralCalendarIcon,
            label: intl.formatMessage({ defaultMessage: 'Flexible cancellation', id: 'rObfLp' }),
            key: 'cancellation',
          }
        : undefined;

    const sleeps: IInfo | undefined = sleepsNum
      ? {
          icon: BED,
          bonfireIcon: AmenitiesBookingSleepsIcon,
          label: intl.formatMessage(
            { defaultMessage: 'Sleeps {beds}', id: 'oxlpEG' },
            { beds: sleepsNum },
          ),
          key: 'sleeps',
        }
      : undefined;
    const beds: IInfo | undefined = bedsNum
      ? {
          icon: BED,
          bonfireIcon: AmenitiesBookingSleepsIcon,
          label: intl.formatMessage(
            { defaultMessage: 'Beds {beds}', id: 'LUCOMX' },
            { beds: bedsNum },
          ),
          key: 'beds',
        }
      : undefined;
    const setupOnly: IInfo | undefined = data.disallow_movement
      ? {
          icon: SETUP_ONLY,
          bonfireIcon: AmenitiesAwningAwningIcon,
          label: intl.formatMessage({ defaultMessage: 'Setup only', id: 'XGNs8R' }),
          key: 'setup-only',
        }
      : undefined;

    const lengthUnit = mapPluralUnitToSingular(data.locale?.length_unit);
    const length: IInfo | undefined =
      lengthUnit &&
      data.vehicle_length &&
      formatMeasurement(data.vehicle_length, { unit: lengthUnit })
        ? {
            icon: ARROW_LEFT_RIGHT,
            bonfireIcon: GeneralLengthIcon,
            label: `${formatMeasurement(data.vehicle_length, { unit: lengthUnit })}. long`,
            key: 'length',
          }
        : undefined;

    const maxVehicleLength: IInfo | undefined =
      data.rental_category === ERentalCategory.CAMPGROUND &&
      data.campsite_category?.category_type === ECampsiteCategoryType.RV_SITE &&
      data.campsite_category?.max_vehicle_length
        ? {
            icon: ARROW_LEFT_RIGHT,
            bonfireIcon: GeneralLengthIcon,
            label: intl.formatMessage(
              { defaultMessage: 'RVs up to {length}{lengthUnit}', id: 'saHenM' },
              {
                length: data.campsite_category.max_vehicle_length,
                lengthUnit: 'ft', // TODO: backend to allow for Locale and meters
              },
            ),
            key: 'max-vehicle-length',
          }
        : undefined;

    const isPetFriendly = (() => {
      if (data.rental_category === ERentalCategory.CAMPGROUND) {
        return data.campground?.features?.allows_pets;
      }

      if (data.rental_category === 'stay') {
        return data.stay?.features.pet_friendly;
      }

      return data.features?.pet_friendly;
    })();

    const petFriendly: IInfo | undefined = isPetFriendly
      ? {
          icon: PET_FRIENDLY,
          bonfireIcon: AmenitiesPetsPetFriendlyIcon,
          label: intl.formatMessage({
            defaultMessage: 'Pet friendly',
            id: 'c8hn0D',
          }),
          key: 'pet-friendly',
        }
      : undefined;

    const hasDelivery = data.delivery && data.delivery_radius > 0;
    const offersDelivery: IInfo | undefined = hasDelivery
      ? {
          icon: RUNNING_VEHICLE,
          bonfireIcon: AmenitiesBookingBookerDeliveryOnlyIcon,
          label: intl.formatMessage({
            defaultMessage: 'Offers delivery',
            id: 'sawrL+',
          }),
          key: 'offers-delivery',
        }
      : undefined;

    const fuelType = {
      icon: data.features?.fuel_type === 'electric' ? BOLT : FUEL,
      label: (() => {
        if (data.features?.fuel_type === 'electric') {
          return intl.formatMessage({ defaultMessage: 'Electric', id: 'iUnbdB' });
        }

        if (data.features?.fuel_type === 'diesel') {
          return intl.formatMessage({ defaultMessage: 'Diesel', id: '8eagko' });
        }

        return intl.formatMessage({ defaultMessage: 'Gasoline', id: 'XjsART' });
      })(),
      key: 'fuel-type',
    };

    const seats =
      isAuto && data.seatbelts
        ? {
            icon: USERS,
            label: intl.formatMessage(
              {
                defaultMessage: '{seats, plural, one {# seat} other {# seats}}',
                id: '5cES5G',
              },
              { seats: data.seatbelts },
            ),
            key: 'seats',
          }
        : undefined;

    const keylessEntry =
      isAuto && (data.features as { keyless_entry: boolean } | undefined)?.keyless_entry
        ? {
            icon: GLOBE,
            label: intl.formatMessage({
              defaultMessage: 'Keyless entry',
              id: 'z+OIdo',
            }),
            key: 'keyless-entry',
          }
        : undefined;

    const minimumAge: IInfo | undefined = data.minimum_renter_age
      ? {
          icon: USER_CHECK,
          bonfireIcon: GeneralUserUserCheckIcon,
          label: intl.formatMessage(
            { defaultMessage: '{age}+', id: 'WehlJw' },
            { age: data.minimum_renter_age },
          ),
          key: 'minimum-age',
        }
      : undefined;

    const infos = (() => {
      if (isAuto) {
        return [fuelType, seats, keylessEntry, minimumAge].filter(Boolean) as IInfo[];
      }

      return data.rental_category === ERentalCategory.STAY ||
        data.rental_category === ERentalCategory.CAMPGROUND
        ? ([sleeps, beds, maxVehicleLength, cancellation, petFriendly, minimumAge].filter(
            Boolean,
          ) as IInfo[])
        : ([setupOnly, guests, sleeps, length, petFriendly, offersDelivery, minimumAge].filter(
            Boolean,
          ) as IInfo[]);
    })();

    const ratingNumber = data.score;
    const reviewsCount = ratingNumber ? data.reviews_num : undefined;
    const isSuperhost = data.tags?.some(tag => tag.slug === 'superhost');

    return {
      title: isAuto
        ? `${data.vehicle_make} ${data.vehicle_model}, ${data.vehicle_year}`
        : data.name,
      infos,
      accommodationType,
      locationPrefix,
      location,
      ratingNumber,
      reviewsCount,
      campsiteCategoryType: data.campsite_category?.category_type,
      dealer: data.dealer,
      isSuperhost,
    };
  },
);
